import {
  AdminMode,
  AircraftClass,
  airportValue,
  BuildingType,
  type BuildingTypeId,
  dayIndex,
  dollars,
  Facility,
  facilityCount,
  GrantState,
  MILESTONES,
  milestoneDone,
  type World,
} from '@am/sim';

/*
 * Account achievements and titles, shared by the server (which decides them) and the client
 * (which shows them). The server reads every save a player uploads, takes the facts below from
 * the world in it, and unlocks what they meet; achievements stay unlocked for good. Points add
 * up to a rank title; some achievements give a title of their own, and the player picks which
 * title others see (leaderboard, profile, co-op).
 *
 * Ids are stored in player profiles: never rename or reuse one.
 */

/** What an uploaded save says about a player's airport. */
export interface AirportFacts {
  /** Airport value (cents), see the sim's airportValue. */
  value: number;
  money: number;
  /** 0–1000. */
  rating: number;
  departures: number;
  widebodyDepartures: number;
  /** Game day, from 1. */
  day: number;
  milestones: number;
  runways: number;
  loans: number;
  coop: boolean;
  airportName: string;
  /** Private and business jets seen off. */
  lightDepartures: number;
  /** Private helicopters seen off. */
  helicopterDepartures: number;
  /** Military sorties: fighters, airlifters and drones. */
  militaryDepartures: number;
  /** The highest completed control tower and terminal levels (0: none). */
  towerLevel: number;
  terminalLevel: number;
  /** Contact stands (jet bridges) and de-icing pads in service. */
  contactStands: number;
  deicePads: number;
  /** Spotters' decks open (visitors watching the departures). */
  spotterDecks: number;
  /** VIPs who left pleased, and air shows flown (older facts have neither). */
  vipsPleased: number;
  airshows: number;
  /** Airlines in the album (that have flown from here). */
  airlinesCollected: number;
  /** A metro station under a terminal. */
  metro: boolean;
  /** A terminal with a lounge and both duty-free shops. */
  comfort: boolean;
  /** Land parcels owned, the starting land included. */
  parcels: number;
  /** Different airlines flying from the airport (flight contracts, not parking rentals). */
  airlines: number;
  /** The state's incentive grant taken and its sign put up in time. */
  grant: boolean;
  /** Admin mode was on in this world (money without end): it counts for nothing. */
  admin: boolean;
}

export function airportFacts(world: World, coop: boolean): AirportFacts {
  let departures = 0;
  for (const n of world.departuresByClass) departures += n;
  const built = (type: BuildingTypeId) =>
    world.buildings.filter((b) => b.type === type && b.level >= 1);
  const best = (type: BuildingTypeId) => built(type).reduce((n, b) => Math.max(n, b.level), 0);
  const terminals = built(BuildingType.Terminal);
  const of = (cls: number) => world.departuresByClass[cls] ?? 0;
  return {
    value: airportValue(world),
    money: world.money,
    rating: world.rating,
    departures,
    widebodyDepartures: of(AircraftClass.Widebody),
    day: dayIndex(world.time) + 1,
    milestones: world.milestones,
    runways: built(BuildingType.Runway).length + built(BuildingType.MainRunway).length,
    loans: world.loans.length,
    coop,
    airportName: world.airportName,
    lightDepartures: of(AircraftClass.Light),
    helicopterDepartures: of(AircraftClass.Helicopter),
    militaryDepartures:
      of(AircraftClass.Fighter) + of(AircraftClass.Airlifter) + of(AircraftClass.Drone),
    towerLevel: best(BuildingType.Tower),
    terminalLevel: best(BuildingType.Terminal),
    contactStands: built(BuildingType.ContactStand).length,
    deicePads: built(BuildingType.DeicePad).length,
    spotterDecks: built(BuildingType.SpotterDeck).length,
    vipsPleased: world.vipsPleased,
    airshows: world.airshows,
    airlinesCollected: world.airlinesSeen.length,
    metro: terminals.some((b) => facilityCount(b.facilities, Facility.Metro) > 0),
    comfort: terminals.some(
      (b) =>
        facilityCount(b.facilities, Facility.Lounge) > 0 &&
        facilityCount(b.facilities, Facility.Shops) >= 2,
    ),
    parcels: world.ground.ownedCount(),
    airlines: new Set(world.contracts.filter((c) => c.rental === 0).map((c) => c.airline)).size,
    grant: world.grantState === GrantState.Fulfilled,
    admin: (world.adminMode & AdminMode.Used) !== 0,
  };
}

const milestone = (key: string) => {
  const def = MILESTONES.find((m) => m.key === key);
  return (f: AirportFacts) => def !== undefined && milestoneDone(f.milestones, def.id);
};

export interface AchievementDef {
  readonly id: string;
  readonly points: number;
  /** A title of its own the player may show (see TITLES), if any. */
  readonly title?: string;
  /** Grouping for the list. */
  readonly group: 'traffic' | 'business' | 'service' | 'growth' | 'build' | 'social';
  readonly met: (f: AirportFacts) => boolean;
}

export const ACHIEVEMENTS: readonly AchievementDef[] = [
  { id: 'firstFlight', points: 10, group: 'traffic', met: (f) => f.departures >= 1 },
  { id: 'flights100', points: 20, group: 'traffic', met: (f) => f.departures >= 100 },
  { id: 'flights1000', points: 40, group: 'traffic', met: (f) => f.departures >= 1000 },
  {
    id: 'flights5000',
    points: 80,
    group: 'traffic',
    title: 'controller',
    met: (f) => f.departures >= 5000,
  },
  { id: 'widebody', points: 30, group: 'traffic', met: (f) => f.widebodyDepartures >= 1 },
  { id: 'privateJet', points: 20, group: 'traffic', met: (f) => f.lightDepartures >= 1 },
  {
    id: 'helicopter',
    points: 20,
    group: 'traffic',
    met: (f) => f.helicopterDepartures >= 1,
  },
  {
    id: 'military',
    points: 40,
    group: 'traffic',
    title: 'commander',
    met: (f) => f.militaryDepartures >= 1,
  },
  { id: 'threeRunways', points: 40, group: 'traffic', met: (f) => f.runways >= 3 },
  { id: 'value1m', points: 15, group: 'business', met: (f) => f.value >= dollars(1_000_000) },
  { id: 'value10m', points: 40, group: 'business', met: (f) => f.value >= dollars(10_000_000) },
  { id: 'value50m', points: 80, group: 'business', met: (f) => f.value >= dollars(50_000_000) },
  {
    id: 'value200m',
    points: 150,
    group: 'business',
    title: 'tycoon',
    met: (f) => f.value >= dollars(200_000_000),
  },
  { id: 'cash10m', points: 50, group: 'business', met: (f) => f.money >= dollars(10_000_000) },
  {
    id: 'debtFree',
    points: 30,
    group: 'business',
    met: (f) => f.loans === 0 && f.money >= 0 && f.value >= dollars(5_000_000),
  },
  { id: 'grant', points: 15, group: 'business', met: (f) => f.grant },
  { id: 'airlines10', points: 30, group: 'business', met: (f) => f.airlines >= 10 },
  { id: 'rating4', points: 25, group: 'service', met: (f) => f.rating >= 800 },
  {
    id: 'rating5',
    points: 60,
    group: 'service',
    title: 'favourite',
    met: (f) => f.rating >= 950,
  },
  { id: 'redCarpet', points: 30, group: 'service', met: (f) => (f.vipsPleased ?? 0) >= 5 },
  {
    id: 'fullService',
    points: 50,
    group: 'service',
    met: (f) => milestone('hotel')(f) && milestone('rail')(f) && milestone('kitchen')(f),
  },
  { id: 'comfort', points: 20, group: 'service', met: (f) => f.comfort },
  { id: 'freight', points: 25, group: 'growth', title: 'cargo', met: milestone('freight') },
  { id: 'hub', points: 40, group: 'growth', title: 'hub', met: milestone('hub') },
  { id: 'international', points: 30, group: 'growth', met: milestone('international') },
  { id: 'charter', points: 20, group: 'growth', met: milestone('charter') },
  { id: 'airshow', points: 25, group: 'growth', met: (f) => (f.airshows ?? 0) >= 1 },
  {
    id: 'collector',
    points: 40,
    group: 'growth',
    met: (f) => (f.airlinesCollected ?? 0) >= 20,
  },
  {
    id: 'allMilestones',
    points: 120,
    group: 'growth',
    title: 'perfectionist',
    met: (f) => MILESTONES.every((m) => milestoneDone(f.milestones, m.id)),
  },
  { id: 'yearOne', points: 20, group: 'growth', met: (f) => f.day > 28 },
  { id: 'veteran', points: 60, group: 'growth', title: 'veteran', met: (f) => f.day > 112 },
  { id: 'towerTop', points: 30, group: 'build', met: (f) => f.towerLevel >= 5 },
  {
    id: 'terminal5',
    points: 50,
    group: 'build',
    title: 'architect',
    met: (f) => f.terminalLevel >= 5,
  },
  { id: 'jetBridges', points: 25, group: 'build', met: (f) => f.contactStands >= 4 },
  { id: 'metro', points: 40, group: 'build', met: (f) => f.metro },
  { id: 'deicePad', points: 20, group: 'build', met: (f) => f.deicePads >= 1 },
  // Facts saved before the deck existed have no count: none then.
  { id: 'spotters', points: 15, group: 'build', met: (f) => (f.spotterDecks ?? 0) >= 1 },
  { id: 'landlord', points: 30, group: 'build', met: (f) => f.parcels >= 20 },
  { id: 'coop', points: 15, group: 'social', title: 'teamPlayer', met: (f) => f.coop },
];

/** Rank titles by achievement points, lowest first. */
export const RANKS: readonly { readonly id: string; readonly points: number }[] = [
  { id: 'rookie', points: 0 },
  { id: 'apron', points: 40 },
  { id: 'tower', points: 120 },
  { id: 'operations', points: 250 },
  { id: 'director', points: 420 },
  { id: 'mogul', points: 650 },
  { id: 'legend', points: 900 },
];

export const MAX_POINTS = ACHIEVEMENTS.reduce((n, a) => n + a.points, 0);

/** Achievements the facts meet. */
export function metAchievements(facts: AirportFacts): string[] {
  return ACHIEVEMENTS.filter((a) => a.met(facts)).map((a) => a.id);
}

export function achievementPoints(unlocked: readonly string[]): number {
  let n = 0;
  for (const a of ACHIEVEMENTS) if (unlocked.includes(a.id)) n += a.points;
  return n;
}

/** The highest rank the points reach. */
export function rankFor(points: number): string {
  let rank = RANKS[0]?.id ?? 'rookie';
  for (const r of RANKS) if (points >= r.points) rank = r.id;
  return rank;
}

/**
 * Titles the player may show: every rank reached, and the titles of their achievements. Title
 * ids: `rank.<id>` and `title.<id>`.
 */
export function availableTitles(unlocked: readonly string[]): string[] {
  const points = achievementPoints(unlocked);
  const titles = RANKS.filter((r) => points >= r.points).map((r) => `rank.${r.id}`);
  for (const a of ACHIEVEMENTS) {
    if (a.title && unlocked.includes(a.id)) titles.push(`title.${a.title}`);
  }
  return titles;
}

/** The title shown for a player: their pick if still theirs, else their rank. */
export function shownTitle(unlocked: readonly string[], picked: string | null): string {
  if (picked && availableTitles(unlocked).includes(picked)) return picked;
  return `rank.${rankFor(achievementPoints(unlocked))}`;
}
